import {
  Alert,
  Breadcrumbs,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { instance } from "../../Const/ApiHeader";
import {
  GET_FORCE_UPDATE_CONFIG,
  UPDATE_FORCE_UPDATE_CONFIG,
} from "../../Const/ApiConst";

const EMPTY_FORM = {
  forceUpdateEnabled: false,
  minVersionAndroid: "1.0.0",
  minVersionIos: "1.0.0",
  playStoreUrl:
    "https://play.google.com/store/apps/details?id=com.power_motoring.app",
  appStoreUrl: "",
  message:
    "A new version of Power Motoring is available. Please update to continue.",
};

function ForceUpdate() {
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);

  const loadConfig = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await instance.get(GET_FORCE_UPDATE_CONFIG);
      setForm({
        forceUpdateEnabled: Boolean(response.data?.forceUpdateEnabled),
        minVersionAndroid: response.data?.minVersionAndroid || "1.0.0",
        minVersionIos: response.data?.minVersionIos || "1.0.0",
        playStoreUrl: response.data?.playStoreUrl || EMPTY_FORM.playStoreUrl,
        appStoreUrl: response.data?.appStoreUrl || "",
        message: response.data?.message || EMPTY_FORM.message,
      });
    } catch (err) {
      console.log(err);
      setError("Failed to load force update settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setSuccess("");
  };

  const handleSave = async () => {
    setSaveLoading(true);
    setError("");
    setSuccess("");
    try {
      await instance.post(UPDATE_FORCE_UPDATE_CONFIG, form);
      setSuccess("Force update settings saved.");
      await loadConfig();
    } catch (err) {
      console.log(err);
      setError("Failed to save force update settings.");
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <div className="vehicles-page">
      <Breadcrumbs aria-label="breadcrumb" className="mb-3">
        <Link to="/">Home</Link>
        <Typography>Force Update</Typography>
      </Breadcrumbs>

      <section className="vehicles-hero">
        <div>
          <Typography className="vehicles-hero-eyebrow">
            <SystemUpdateAltIcon fontSize="inherit" /> Mobile app
          </Typography>
          <Typography variant="h4" className="vehicles-hero-title">
            Force Update
          </Typography>
          <Typography className="vehicles-hero-subtitle">
            Require users on older app versions to update before using the app.
          </Typography>
        </div>
        <Chip
          label={form.forceUpdateEnabled ? "Enforcement on" : "Enforcement off"}
          color={form.forceUpdateEnabled ? "warning" : "default"}
        />
      </section>

      {error ? (
        <Alert severity="error" className="mt-3">
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" className="mt-3">
          {success}
        </Alert>
      ) : null}

      {loading ? (
        <div className="section force-update-loading">
          <CircularProgress size={32} />
          <Typography color="text.secondary">Loading settings...</Typography>
        </div>
      ) : (
        <section className="section force-update-form">
          <FormControlLabel
            control={
              <Switch
                checked={form.forceUpdateEnabled}
                onChange={(e) =>
                  updateField("forceUpdateEnabled", e.target.checked)
                }
              />
            }
            label="Enable force update for outdated app versions"
          />

          <div className="force-update-fields">
            <TextField
              label="Minimum Android version"
              placeholder="e.g. 8.1.2"
              value={form.minVersionAndroid}
              onChange={(e) => updateField("minVersionAndroid", e.target.value)}
              fullWidth
              size="small"
              helperText="Users below this version must update (Android)."
            />
            <TextField
              label="Minimum iOS version"
              placeholder="e.g. 8.1.2"
              value={form.minVersionIos}
              onChange={(e) => updateField("minVersionIos", e.target.value)}
              fullWidth
              size="small"
              helperText="Users below this version must update (iOS)."
            />
            <TextField
              label="Google Play Store URL"
              value={form.playStoreUrl}
              onChange={(e) => updateField("playStoreUrl", e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Apple App Store URL"
              value={form.appStoreUrl}
              onChange={(e) => updateField("appStoreUrl", e.target.value)}
              fullWidth
              size="small"
              helperText="Optional. Used on iOS when provided."
            />
            <TextField
              label="Update message"
              value={form.message}
              onChange={(e) => updateField("message", e.target.value)}
              fullWidth
              multiline
              minRows={3}
              helperText="Shown on the blocking update screen in the mobile app."
            />
          </div>

          <div className="force-update-actions">
            <LoadingButton
              variant="contained"
              loading={saveLoading}
              onClick={handleSave}
            >
              Save settings
            </LoadingButton>
          </div>
        </section>
      )}
    </div>
  );
}

export default ForceUpdate;
